import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import moment from 'moment';

// NETWORKING
import useBillingSummaryRequest from '../../requests/BillingsSummaryRequest';

// MESSAGES
import BillingsSummaryFormattedMessages from '../../messages/billing/BillingsSummaryFormattedMessages';

// ROUTES
import { BillingRoutes } from '../../routes/BillingRoutes';

// COMPONENTS
import Layout from '../../components/layouts/Layout';
import BillingSummaryTable from '../../components/billing/summary/BillingSummaryTable';
import BillingSummaryTableRow from '../../components/billing/summary/BillingSummaryTableRow';
import Loader from '../../components/common/Loader';
import MonthYearSelector from '../../components/common/MonthYearSelector';

const BillingsSummary = ({
  customer,
  incomingData,
  errorHandler,
  accessLevel,
  accessType,
  acting_names,
}) => {
  const { getData } = useBillingSummaryRequest();
  const [list, setList] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (customer && customer !== null) {
      if (incomingData && incomingData.length !== 0) {
        console.log('Have incoming data:', incomingData);
        setData(incomingData);
      } else {
        // else if no data, fetch initial set
        getReportData();
      }
    }
  }, [customer, incomingData, errorHandler, selectedMonth, selectedYear]);

  const getReportData = async () => {
    setLoading(true);
    const result = await getData(selectedMonth, selectedYear, errorHandler);
    setData(result);
  };

  const formatData = useCallback(async () => {
    try {
      let totals = {
        SetupFees: 0,
        Licence: 0,
        Messages: 0,
        Replies: 0,
        MessageFees: 0,
        TotalFees: 0,
        GST: 0,
        Surcharge: 0,
        Total: 0,
        SetupFeesDisplay: 0,
        LicenceDisplay: 0,
        MessageFeesDisplay: 0,
        TotalFeesDisplay: 0,
        GSTDisplay: 0,
        CCSurchargeDisplay: 0,
        TotalDisplay: 0,
      };

      if (!data) {
        setLoading(false);
        return null;
      } else {
        let dataFormatted = data.map((el, index) => {
          el.bill_date = moment(el.bill_date).format('DD-MMM-YY');
          if (el.paid_date) {
            el.paid_date = moment(el.paid_date).format('DD-MMM-YY');
          }
          console.log('currency: ', el.plan_currency);
          let setupFees = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.setup_fees);
          let licenseFees = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.licence_fees);
          let messageFees = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.message_fees);
          let totalExGST = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.total_ex_gst);
          let gstTotal = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.gst_total);
          let surchargeTotal = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.credit_card_surcharge);
          let totalIncGST = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: el.plan_currency,
          }).format(el.total_inc_gst);

          return (
            <BillingSummaryTableRow
              key={`row_${index}`}
              id={index}
              date={el.bill_date}
              invoiceNo={el.invoice_no}
              name={el.customer_name}
              COO={el.coo_flag}
              paidDate={el.paid_date}
              paymentType={el.payment_type}
              suffix={el.cc_number_suffix}
              setupFees={setupFees}
              licenceFees={licenseFees}
              messages={el.message_count}
              replies={el.reply_count}
              messageFees={messageFees}
              totalFees={totalExGST}
              gst={gstTotal}
              surcharge={surchargeTotal}
              total={totalIncGST}
            />
          );
        });

        // Add totals
        data.forEach((el) => {
          totals.SetupFees = totals.SetupFees + el.setup_fees;
          totals.Licence = totals.Licence + el.licence_fees;
          totals.Messages = totals.Messages + el.message_count;
          totals.Replies = totals.Replies + el.reply_count;
          totals.MessageFees = totals.MessageFees + el.message_fees;
          totals.TotalFees = totals.TotalFees + el.total_ex_gst;
          totals.GST = totals.GST + el.gst_total;
          totals.Surcharge = totals.Surcharge + el.credit_card_surcharge;
          totals.Total = totals.Total + el.total_inc_gst;
        });

        // Format totals
        totals.SetupFeesDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.SetupFees);
        totals.LicenceDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.Licence);
        totals.MessageFeesDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.MessageFees);
        totals.TotalFeesDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.TotalFees);
        totals.GSTDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.GST);
        totals.CCSurchargeDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.Surcharge);
        totals.TotalDisplay = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(totals.Total);
        console.log('totals.SetupFees');
        console.log(totals.SetupFees);

        console.table(data);
        setList(dataFormatted);
        setTotals(totals);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      console.log('data error:', e);
    }
  }, [data]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'selectedMonth') {
      setSelectedMonth(Number(value));
    } else if (name === 'selectedYear') {
      setSelectedYear(Number(value));
    }
  };

  useEffect(() => {
    if (data) {
      formatData();
    }
  }, [formatData]);

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        routes={BillingRoutes}
        title={BillingsSummaryFormattedMessages.billingsSummaryTitle}
        accessLevel={accessLevel}
        accessType={accessType}
        customerType={customer.customer_type}
        backgroundColor="none"
        titleRowRightComponent={
          <Box
            sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}
          >
            <MonthYearSelector
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              handleInputChange={handleInputChange}
            />
          </Box>
        }
      >
        <Typography gutterBottom>
          Provider: {acting_names?.provider_name}
        </Typography>

        {loading ? (
          <Loader />
        ) : (
          <BillingSummaryTable
            changePage={() => {}}
            totals={totals}
            list={list}
            handlePagination={() => {}}
            pageCount={pageCount}
          />
        )}
      </Layout>
    );
  }
  return null;
};

export default BillingsSummary;
